
import { SingleIndex } from '@deqo/foundation';
import { telemetry } from '../../../@vendor/Posthog';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { Line } from '../../../platform/geom/Line';
import { toRadians } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { EditorSession } from '../../canvas/EditorSession';
import { RootOverride } from '../../canvas/RootOverride';
import { Selection } from '../../canvas/Selection';
import { resolveSubject, resolveSubjects } from '../../canvas/commands/CommandUtil';
import { place } from '../../canvas/commands/OrderCommands';
import { Phys } from '../../canvas/entities/Phys';
import { RelativeTo } from '../../canvas/entities/Physical';
import { Root } from '../../canvas/entities/Root';
import { toolEquip } from '../../canvas/tooling/ToolCommands';
import { ToolInfo } from '../../canvas/tooling/ToolInfo';
import { ConnectTarget } from './ConnectTarget';
import { Connector } from './Connector';
import { ConnectorCapStyle, ConnectorMode } from './ConnectorEnums';

export const connectorCreateFromSelection = defineCommand(
    'connectorCreateFromSelection',
    'Connector: Create From Selection',
    (cc) => {

        const session = cc.resolve(EditorSession);
        const targets = session.get(Selection).polarize(ConnectTarget);
        if (!targets.length) return;

        cc.dispatch(toolEquip, ['connectorTool', 'plop', true]);
        telemetry.emit('interaction.connectorCreateFromSelection');
    }
);

export const connectorCreateBetween = defineCommand(
    'connectorCreateBetween',
    'Connector: Create Between',
    (cc, [firstId, secondId]: [string, string]) => {

        const objects = cc.resolve(ObjectManager);
        const session = cc.resolve(EditorSession);
        const root = session.get(RootOverride) ?? objects.get('root', Root);

        const first = resolveSubject(cc, firstId, ConnectTarget);
        const second = resolveSubject(cc, secondId, ConnectTarget);
        if (!first || !second) return;

        objects.update(u => {
            const conn = u.create(Connector);
            conn.fromId = first.id;
            conn.toId = second.id;
            conn.endCap = ConnectorCapStyle.solidArrow;
            conn.fromOffset = new Point(.5, .5);
            conn.toOffset = new Point(.5, .5);
            root.add(conn);
            conn.applyOffsets();
            cc.dispatch(place, [[conn.id]]);
        });

        telemetry.emit('interaction.connectorCreateBetween');
    }
);

export const connectorConnect = defineCommand(
    'connectorConnect',
    'Connector: Connect',
    (cc, [connectorId, side, targetId, offset]: [string, 'start' | 'end', string, Point]) => {

        const connector = resolveSubject(cc, connectorId)?.as(Connector);
        const target = resolveSubject(cc, targetId, ConnectTarget);
        if (!connector || !target) return;

        const targetBounds = target.bounds(RelativeTo.Model);
        const objects = cc.resolve(ObjectManager);

        const toFactor = (pt: Point) => new Point(
            pt.x / targetBounds.width,
            pt.y / targetBounds.height
        );

        objects.update(() => {

            if (side === 'start') {
                connector.fromId = targetId;
                connector.fromOffset = toFactor(offset);
            }
            else {
                connector.toId = targetId;
                connector.toOffset = toFactor(offset);
            }

            if (connector.fromId === connector.toId) {
                connector.fromId = null;
                connector.fromOffset = null;
                connector.fromRotation = 0;
                connector.toId = null;
                connector.toOffset = null;
                connector.toRotation = 0;
            }
        });

        telemetry.emit('interaction.connectorConnect');
    }
);

export const connectorDisconnect = defineCommand(
    'connectorDisconnect',
    'Connector: Disconnect',
    (cc, [connectorId, side]: [string, 'start' | 'end']) => {

        const connector = resolveSubject(cc, connectorId)?.as(Connector);
        if (!connector) return;

        const objects = cc.resolve(ObjectManager);

        objects.update(() => {
            if (side === 'start') {
                connector.fromId = null;
                connector.fromOffset = null;
                connector.fromRotation = 0;
            }
            else {
                connector.toId = null;
                connector.toOffset = null;
                connector.toRotation = 0;
            }
        });

        telemetry.emit('interaction.connectorDisconnect');
    }
);

export const connectorRotate = defineCommand(
    'connectorRotate',
    'Connector: Rotate',
    (cc, [connectorId, side]: [string, 'start' | 'end']) => {

        const connector = resolveSubject(cc, connectorId)?.as(Connector);
        if (!connector) return;

        const objects = cc.resolve(ObjectManager);

        const { mode } = connector;

        objects.update(() => {
            const inc = mode === ConnectorMode.smooth ? toRadians(-90) : 0;
            if (side === 'start') {
                connector.fromRotation = connector.fromRotation + inc;
            }
            else {
                connector.toRotation = connector.toRotation + inc;
            }
        });

        telemetry.emit('interaction.connectorDisconnect');
    }
);

export const connectorSpread = defineCommand(
    'connectorSpread',
    'Connector: Spread',
    (cc, [targetIds]: [string[]?]) => {

        const connectTargets = resolveSubjects(cc, targetIds, ConnectTarget);
        if (!connectTargets.length) return;

        const objects = cc.resolve(ObjectManager);
        objects.update(() => {

            for (const target of connectTargets) {

                const allConnectors = Phys.from(target.relations('attached'))
                    .polarize(Connector)
                    .toArray()
                    .sort((a, b) => a.fromOffset.y - b.fromOffset.y);

                const targetBounds = target.bounds(RelativeTo.Model);
                const targetEdges = targetBounds.inflate(-10).lines();
                const groups = new SingleIndex<Line, Connector>();

                for (const c of allConnectors) {
                    const connectorSegment = Line.from(c.createFigure());
                    const intersectingEdge = targetEdges.find(x => x.intersects(connectorSegment));
                    if (!intersectingEdge) continue;
                    groups.add(intersectingEdge, c);
                }

                for (const [edge, connectors] of groups) {

                    const ev = edge.toVector().normalize();
                    const evi = Point.zero.add(ev.multiply(-999999999));

                    const ordered = Array.from(connectors)
                        .sort((a, b) => {
                            const ad = a.side('opposite', target.id).node.project(ev).distanceTo(evi);
                            const bd = b.side('opposite', target.id).node.project(ev).distanceTo(evi);
                            return ad - bd;
                        });

                    const s = (ordered.length * 20) - 20;
                    const es = edge.length / 2 - s / 2;

                    for (const c of ordered) {

                        const dist = es + (ordered.indexOf(c) * 20);
                        const newAbsOffset = edge.pointAt(dist);
                        const newOffset = targetBounds.percentagePointAt(newAbsOffset.subtract(targetBounds.topLeft()));

                        if (c.fromId === target.id) {
                            c.fromOffset = newOffset;
                        }
                        else {
                            c.toOffset = newOffset;
                        }

                        c.applyOffsets();
                    }
                }
            }

        });

        telemetry.emit('interaction.connectorSpread');
    }
);

export const connectorBunch = defineCommand(
    'connectorBunch',
    'Connector: Bunch',
    (cc, [targetIds]: [string[]?]) => {

        const connectTargets = resolveSubjects(cc, targetIds, ConnectTarget);
        if (!connectTargets.length) return;

        const objects = cc.resolve(ObjectManager);
        objects.update(() => {

            for (const target of connectTargets) {

                const allConnectors = Phys.from(target.relations('attached')).polarize(Connector);

                const targetBounds = target.bounds(RelativeTo.Model);
                const targetEdges = targetBounds.inflate(-10).lines();
                const groups = new SingleIndex<Line, Connector>();

                for (const c of allConnectors) {
                    const connectorSegment = Line.from(c.createFigure());
                    const intersectingEdge = targetEdges.find(x => x.intersects(connectorSegment));
                    if (!intersectingEdge) continue;
                    groups.add(intersectingEdge, c);
                }

                for (const [edge, connectors] of groups) {

                    const newAbsOffset = edge.lerp(.5);
                    const newOffset = targetBounds.percentagePointAt(newAbsOffset.subtract(targetBounds.topLeft()));

                    for (const c of connectors) {

                        if (c.fromId === target.id) {
                            c.fromOffset = newOffset;
                        }
                        else {
                            c.toOffset = newOffset;
                        }

                        c.applyOffsets();
                    }
                }
            }

        });

        telemetry.emit('interaction.connectorBunch');
    }
);

export const connectorToolSpecialEquip = defineCommand(
    'connectorToolSpecialEquip',
    null,
    cc => {

        // Maintain the selection...
        const session = cc.resolve(EditorSession);
        const currentTool = session.get(ToolInfo);

        if (currentTool?.id !== 'connectorTool') {
            const selection = session.get(Selection);
            cc.dispatch(toolEquip, ['connectorTool']);
            session.update(Selection, selection);
        }
    }
);

export const inConnectorCommands = () => inCommands(
    connectorCreateFromSelection,
    connectorConnect,
    connectorDisconnect,
    connectorSpread,
    connectorBunch,
);

